import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Gallery, Modal, useMediaQuery } from '@printdeal/ui-library';
import { graphql } from 'gatsby';
import { dispatchDataLayerEntry } from '../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { ImageComponent } from '../../../types/product/productDetailPage';

/** @knipignore */
export const query = graphql`
fragment Gallery on ContentfulGallery {
  images {
    id: contentful_id
    title
    url
  }
  enlargedImages: images {
    id: contentful_id
    title
    url
  }
  thumbnails: images {
    id: contentful_id
    url
  }
  sceneTypes
  assets {
    __typename
   ... on ContentfulGalleryAsset {
      id: contentful_id
      video
      image {
        id: contentful_id
        title
        url
      }
    }
    ... on ContentfulInteractiveImage {
      id: contentful_id
      image: imageMobile {
        interactiveImageId: id
        bannerURL
        hotspots {
          title
          content
          featuredImageURL
          url
          percentageX
          percentageY
        }
      }
      thumbnailImage: imageMobile {
        bannerURL
      }
      enlargedImage: imageMobile {
        bannerURL
      }
    }
  }
}
`;
interface ProductGalleryProps {
  imageComponents: ImageComponent[];
  thumbnailComponents: ImageComponent[];
  enlargedImageComponents: ImageComponent[];
  pageName: string
  modalTitle: string;
  controls?: {
    isNavExpandable?: boolean;
    isOpenModal?: boolean;
    setIsOpenModal?: (state: boolean) => void;
    currentItem?: number;
    setCurrentItem?: (state: number) => void;
  };
  maxThumbnails?: number;
}

export const messages = {
  prev: { id: 'general.pagination.button.previous', defaultMessage: 'Previous' },
  next: { id: 'general.pagination.button.previous', defaultMessage: 'Next' },
  zoom: { id: 'general.imageGallery.button.zoom', defaultMessage: 'Zoom' },
  close: { id: 'generic.modal.close', defaultMessage: 'Close' },
};

export const ProductGallery = ({
  imageComponents,
  thumbnailComponents,
  enlargedImageComponents,
  pageName,
  modalTitle,
  controls = {
    isNavExpandable: true,
    isOpenModal: false,
    setIsOpenModal: () => {},
    currentItem: 1,
    setCurrentItem: () => {},
  },
  maxThumbnails = 4,
}: ProductGalleryProps) => {
  const intl = useIntl();
  const nextButtonRef = useRef(null);
  const {
    prev, next, zoom, close,
  } = messages;

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [currentItem, setCurrentItem] = useState(1);
  const isDesktop = useMediaQuery('(min-width: 992px)');

  const onEnlargeImage = () => {
    if (controls.setIsOpenModal) {
      controls.setIsOpenModal(true);
    } else {
      setIsOpenModal(true);
    }
    dispatchDataLayerEntry(pageName, 'image_gallery', { type: 'enlarge' });
  };

  const onSlideImage = (direction: string) => {
    dispatchDataLayerEntry(pageName, 'image_gallery', { type: `click_${direction}` });
  };

  const onThumbnailImageClick = (contentfulId: string, position: number) => () => {
    dispatchDataLayerEntry(pageName, 'image_gallery', { type: 'click_thumbnail', contentfulId, position });
  };

  const closeModal = () => {
    if (controls.setIsOpenModal) {
      controls.setIsOpenModal(false);
    } else {
      setIsOpenModal(false);
    }
  };

  return (
    <>
      <Gallery
        id="product-gallery"
        defaultActiveItem={controls.currentItem || currentItem}
        onChangeActiveItem={
          (activeItem) => (controls.setCurrentItem ? controls.setCurrentItem(activeItem) : setCurrentItem(activeItem))
        }
        prevButton={<Gallery.Button variant="prev" screenReaderLabel={intl.formatMessage(prev)} onClick={() => onSlideImage('previous')} />}
        nextButton={<Gallery.Button variant="next" screenReaderLabel={intl.formatMessage(next)} onClick={() => onSlideImage('next')} />}
        zoomButton={<Gallery.Button variant="zoom" screenReaderLabel={intl.formatMessage(zoom)} onClick={onEnlargeImage} />}
      >
        <Gallery.Root>
          {imageComponents.map((imageComponent, index) => (
            // To prevent DOM mismatch issues with image arrays update dynamically due to previews.
            // eslint-disable-next-line react/no-array-index-key
            <Gallery.Item key={`${imageComponent.key}-${index}`}>{imageComponent.element}</Gallery.Item>
          ))}
        </Gallery.Root>
        {isDesktop && (
          <Gallery.Navigation maxItems={maxThumbnails} isNavExpandable={controls.isNavExpandable} className="pd-mt-6">
            {thumbnailComponents.map((thumbnailComponent, index) => (
              <Gallery.Navigation.Item
                // To prevent DOM mismatch issues with image arrays update dynamically due to previews.
                // eslint-disable-next-line react/no-array-index-key
                key={`${thumbnailComponent.key}-${index}`}
                onClick={onThumbnailImageClick(String(thumbnailComponent.key), index + 1)}
              >
                {thumbnailComponent.element}
              </Gallery.Navigation.Item>
            ))}
          </Gallery.Navigation>
        )}
      </Gallery>
      <Modal
        size="lg"
        title={modalTitle}
        isOpen={controls.isOpenModal || isOpenModal}
        onClose={closeModal}
        initialFocus={nextButtonRef}
        dismissLabel={intl.formatMessage(close)}
      >
        <Gallery
          id="zoomed-product-gallery"
          defaultActiveItem={controls.currentItem || currentItem}
          prevButton={<Gallery.Button variant="prev" screenReaderLabel={intl.formatMessage(prev)} />}
          nextButton={<Gallery.Button variant="next" screenReaderLabel={intl.formatMessage(next)} />}
        >
          <Modal.Content>
            <Gallery.Root>
              {enlargedImageComponents.map((enlargedImageComponent, index) => (
                // To prevent DOM mismatch issues with image arrays update dynamically due to previews.
                // eslint-disable-next-line react/no-array-index-key
                <Gallery.Item key={`${enlargedImageComponent.key}-${index}`}>
                  {enlargedImageComponent.element}
                </Gallery.Item>
              ))}
            </Gallery.Root>
          </Modal.Content>
          {isDesktop && (
            <Modal.Footer>
              <Gallery.Navigation className="pd-justify-center">
                {thumbnailComponents.map((thumbnailComponent, index) => (
                  // To prevent DOM mismatch issues with image arrays update dynamically due to previews.
                  // eslint-disable-next-line react/no-array-index-key
                  <Gallery.Navigation.Item key={`${thumbnailComponent.key}-${index}`}>
                    {thumbnailComponent.element}
                  </Gallery.Navigation.Item>
                ))}
              </Gallery.Navigation>
            </Modal.Footer>
          )}
        </Gallery>
      </Modal>
    </>
  );
};
